import { Box, Breadcrumbs, Button, Grid, Paper, Stack, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/system';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import strings from 'common/Translation/Translate';
import DrawerMenu from 'components/DrawerMenu';
import Form, { FormDataModel } from 'components/Form';
import dayjs from 'dayjs';
// import { determinePartOfDay } from 'utils/validators';
import React, { FC, ReactNode, RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useDispatch } from 'react-redux';
import { setOpenPanelAddon } from 'utils/redux/reducer/booking-slice';
import { getLineItemDrawerWidth, removeErrorFieldsFromValues } from 'utils/validators/HelperFunctions';
import { lineItemsForm, lineItemsTaxForm } from './MultipleLineItemsForm';
import { NewBookingAddon } from './NewBookingAddOn';
import { fetchFromStorage } from 'utils/storage';
import PricingStrategySection, {
  PricingStrategyValues,
  buildHourlyRentalBreakdown,
  calcHourlyTotalRental,
  getEffectivePricePerHour,
} from 'components/PricingStrategySection';
import { isCustomizePackage, normalizeCustomPricePackageName } from 'utils/pricePackage';
import { formatTimeForApi, parseTimeValue } from 'components/TimeInput/TimeInput';
import VenueDetailsPageSummary from './VenueDetailsPageSummary';
import {
  buildLineItemPayloadForCosting,
  missingLineItemCostingLabels,
  VENUE_DETAILS_COSTING_DEBOUNCE_MS,
} from './venueDetailsCostingUtils';

export type VenueDetailsPageHeader = {
  customerName: string;
  eventTypeLabel: string;
  dateRangeLabel: string;
};

const emptyPricing = (): PricingStrategyValues => ({
  overridePricePerPerson: false,
  pricePerPerson: null,
  overrideMinimumRevenue: false,
  minimumRevenue: null,
  overrideHallRental: false,
  hallRental: null,
  overridePricePerHour: false,
  pricePerHour: null,
});

interface LineItemDrawerComponentProps {
  openDrawer: boolean;
  setOpenDrawer: (a: boolean) => void;
  pageHeader?: VenueDetailsPageHeader;
  costingPageResponse?: Record<string, any>;
  costingLoading?: boolean;
  costingError?: string | null;
  onRefreshCosting?: (
    draftLineItem: Record<string, any> | null,
    selectedRowId: number | null,
  ) => void | Promise<void>;
  resolveLineItemTotalOnSave?: (
    lineItemPayload: Record<string, any>,
    selectedRowId: number | null,
  ) => Promise<
    | { ok: true; total: string; totalUnformatted: number | null }
    | { ok: false; message: string }
    | null
  >;
  isEditDrawer: boolean;
  isCloneDrawer: boolean;
  setIsEditDrawer: (a: boolean) => void;
  setIsCloneDrawer: (a: boolean) => void;
  lineItemFormRef: any;
  lineItemsPageResponse: any;
  setLineItemsPageResponse: any;
  setBookedAddOns: any;
  setLayoutList: any;
  setPricePackageList: any;
  setTaxGroupList: any;
  setAddonsList: any;
  setSelectedVenue: any;
  setMessage: any;
  setHasError: any;
  bookedAddOns: any;
  lineItems: any;
  pricePackageList: any;
  selectedRowId: any;
  setSelectedRowId: any;
  maxCount: any;
  setMaxCount: any;
  setLineItems: any;
  hasError: any;
  layoutList: any;
  taxGroupList: any;
  venueList: any;
  statusList: any;
  addonsList: any;
  handleLineItemFormChange: any;
  serviceChargeList: any;
  setServiceChargeList: any;
  selectedStartDate: any;
  selectedEndDate: any;
  hourlyPricing: any;
  defaultPricePerPerson: any;
  setDefaultPricePerPerson: any;
  defaultMinimumRevenue: any;
  setDefaultMinimumRevenue: any;
  defaultPricePerHour?: any;
  fixedHallRental: any;
  setFixedHallRental: any;
  corporateBooking: 'true' | 'false';
  branchId: string;
  getCorporateRates: (body: any) => Promise<void>;
  restrictPriceOverride: boolean;
  isOdc: boolean;
  partOfDays: any[];
  eventSubtypeOptions: any[];
  pastBookingWindowDays?: number | null;
  venueMaxCapacity?: number | null;
}

const LineItemDrawerComponent: FC<LineItemDrawerComponentProps> = ({
  openDrawer,
  setOpenDrawer,
  pageHeader,
  costingPageResponse,
  costingLoading = false,
  costingError = null,
  onRefreshCosting,
  resolveLineItemTotalOnSave,
  hourlyPricing,
  isEditDrawer,
  isCloneDrawer,
  setIsEditDrawer,
  setIsCloneDrawer,
  lineItemFormRef,
  lineItemsPageResponse,
  setLineItemsPageResponse,
  setBookedAddOns,
  setLayoutList,
  setPricePackageList,
  setTaxGroupList,
  setAddonsList,
  setSelectedVenue,
  setMessage,
  setHasError,
  bookedAddOns,
  lineItems,
  pricePackageList,
  taxGroupList,
  selectedRowId,
  setSelectedRowId,
  maxCount,
  setMaxCount,
  setLineItems,
  hasError,
  layoutList,
  venueList,
  statusList,
  addonsList,
  handleLineItemFormChange,
  serviceChargeList,
  setServiceChargeList,
  selectedStartDate,
  selectedEndDate,
  defaultPricePerPerson,
  setDefaultPricePerPerson,
  defaultMinimumRevenue,
  setDefaultMinimumRevenue,
  defaultPricePerHour,
  fixedHallRental,
  setFixedHallRental,
  corporateBooking,
  branchId,
  getCorporateRates,
  restrictPriceOverride,
  isOdc,
  partOfDays,
  eventSubtypeOptions,
  pastBookingWindowDays,
  venueMaxCapacity,
}) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const screenSizeDownSm = useMediaQuery(theme.breakpoints.down('sm'));
  const screenSizeDownMd = useMediaQuery(theme.breakpoints.down('md'));
  const serviceChargeDropdown = JSON.parse(fetchFromStorage('serviceChargeDropdown') as any);
  const [pricing, setPricing] = useState<PricingStrategyValues>(emptyPricing());
  // Tax/notes form (Part 2) lives below the pricing strategy controls. It has
  // its own ref so we can read its data + validity on save, then merge with
  // Part 1 before building the request payload.
  const lineItemTaxFormRef = useRef<any>(null);
  const [isSavingLineItem, setIsSavingLineItem] = useState(false);
  const savingRef = useRef(false);

  const hourlyPricingBreakdown = useMemo(() => {
    if (!hourlyPricing) return undefined;
    const rate = getEffectivePricePerHour(pricing, defaultPricePerHour, lineItemsPageResponse?.pricePerHour);
    return buildHourlyRentalBreakdown(
      lineItemsPageResponse?.startTime,
      lineItemsPageResponse?.endTime,
      rate,
    );
  }, [
    hourlyPricing,
    pricing.overridePricePerHour,
    pricing.pricePerHour,
    defaultPricePerHour,
    lineItemsPageResponse?.startTime,
    lineItemsPageResponse?.endTime,
    lineItemsPageResponse?.pricePerHour,
  ]);

  const hourlyTotalRental = useMemo(() => {
    if (!hourlyPricing) return null;
    const rate = getEffectivePricePerHour(pricing, defaultPricePerHour, lineItemsPageResponse?.pricePerHour);
    const total = calcHourlyTotalRental(
      lineItemsPageResponse?.startTime,
      lineItemsPageResponse?.endTime,
      rate,
    );
    return total === '' ? null : total;
  }, [
    hourlyPricing,
    pricing.overridePricePerHour,
    pricing.pricePerHour,
    defaultPricePerHour,
    lineItemsPageResponse?.startTime,
    lineItemsPageResponse?.endTime,
    lineItemsPageResponse?.pricePerHour,
  ]);

  const collectDraftLineItemForCosting = useCallback(() => {
    const part1 = lineItemFormRef.current as
      | { getFormData: () => { formData: FormDataModel } }
      | null
      | undefined;
    const part2 = lineItemTaxFormRef.current as
      | { getFormData: () => { formData: FormDataModel } }
      | null
      | undefined;
    if (!part1?.getFormData) return null;
    const formData = {
      ...part1.getFormData().formData,
      ...(part2?.getFormData?.().formData ?? {}),
    };
    return buildLineItemPayloadForCosting({
      formData,
      pricing,
      bookedAddOns,
      hourlyPricing,
      pricePackageList,
    });
  }, [pricing, bookedAddOns, hourlyPricing, pricePackageList]);

  const onRefreshCostingRef = useRef(onRefreshCosting);
  const collectDraftLineItemForCostingRef = useRef(collectDraftLineItemForCosting);
  const selectedRowIdRef = useRef(selectedRowId);
  const costingDebounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    onRefreshCostingRef.current = onRefreshCosting;
    collectDraftLineItemForCostingRef.current = collectDraftLineItemForCosting;
    selectedRowIdRef.current = selectedRowId;
  });

  const scheduleCostingRefresh = useCallback(() => {
    if (costingDebounceTimerRef.current) {
      clearTimeout(costingDebounceTimerRef.current);
    }
    costingDebounceTimerRef.current = setTimeout(() => {
      costingDebounceTimerRef.current = null;
      if (!onRefreshCostingRef.current) return;
      const draft = collectDraftLineItemForCostingRef.current();
      // Wait until mandatory venue fields are filled before calling costing.
      if (!draft) {
        void onRefreshCostingRef.current(null, null);
        return;
      }
      void onRefreshCostingRef.current(draft, selectedRowIdRef.current);
    }, VENUE_DETAILS_COSTING_DEBOUNCE_MS);
  }, []);

  useEffect(() => {
    if (!openDrawer || !onRefreshCosting) return;
    scheduleCostingRefresh();
  }, [openDrawer, lineItemsPageResponse, pricing, bookedAddOns, hourlyTotalRental, onRefreshCosting, scheduleCostingRefresh]);

  useEffect(
    () => () => {
      if (costingDebounceTimerRef.current) {
        clearTimeout(costingDebounceTimerRef.current);
      }
    },
    [],
  );

  useEffect(() => {
    if (!openDrawer) {
      savingRef.current = false;
      setIsSavingLineItem(false);
    }
  }, [openDrawer]);

  const handlePricingChange = (next: PricingStrategyValues) => {
    setPricing(next);
  };

  // Reset the pricing override panel whenever we open a fresh drawer or
  // switch into edit mode for a different line item. Otherwise the previous
  // selection bleeds in and the user sees stale checkboxes.
  useEffect(() => {
    if (!openDrawer) return;
    setPricing({
      overridePricePerPerson: !!lineItemsPageResponse?.overridePricePerPerson,
      pricePerPerson: lineItemsPageResponse?.pricePerPerson ?? null,
      overrideMinimumRevenue: !!lineItemsPageResponse?.overrideMinimumRevenue,
      minimumRevenue: lineItemsPageResponse?.minimumRevenue ?? null,
      overrideHallRental: !!lineItemsPageResponse?.overrideHallRental,
      hallRental: lineItemsPageResponse?.hallRental ?? null,
      overridePricePerHour: !!lineItemsPageResponse?.overridePricePerHour,
      pricePerHour: lineItemsPageResponse?.pricePerHour ?? null,
    });
    // We intentionally key this only on `openDrawer` + `selectedRowId` because
    // `lineItemsPageResponse` itself updates as the user types in fields that
    // are unrelated to pricing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openDrawer, selectedRowId]);

  const resetLineItemFormState = () => {
    dispatch(setOpenPanelAddon(undefined));
    setIsEditDrawer(false);
    setIsCloneDrawer(false);
    setSelectedVenue('');
    setDefaultPricePerPerson('');
    setDefaultMinimumRevenue('');
    setFixedHallRental(undefined);
    setHasError(false);
    setLineItemsPageResponse({});
    setBookedAddOns([]);
    setAddonsList([]);
    setSelectedRowId(null);
    setPricing(emptyPricing());
  };

  const handleClose = () => {
    resetLineItemFormState();
    setOpenDrawer(false);
  };

  const headerVenueLabel = useMemo(() => {
    const venueValue = lineItemsPageResponse?.venue;
    if (venueValue == null || venueValue === '') return '';
    if (typeof venueValue === 'object' && venueValue !== null) {
      return String((venueValue as { label?: string }).label ?? '').trim();
    }
    return (
      (venueList ?? []).find((v: any) => String(v.value) === String(venueValue))?.label ??
      String(venueValue)
    ).trim();
  }, [lineItemsPageResponse?.venue, venueList]);

  const headerMetaItems = useMemo(() => {
    const items: { icon: ReactNode; label: string }[] = [];
    if (pageHeader?.customerName?.trim()) {
      items.push({
        icon: <PersonOutlineOutlinedIcon sx={{ fontSize: 15 }} />,
        label: pageHeader.customerName.trim(),
      });
    }
    if (pageHeader?.eventTypeLabel?.trim()) {
      items.push({
        icon: <EventOutlinedIcon sx={{ fontSize: 15 }} />,
        label: pageHeader.eventTypeLabel.trim(),
      });
    }
    if (headerVenueLabel) {
      items.push({
        icon: <PlaceOutlinedIcon sx={{ fontSize: 15 }} />,
        label: headerVenueLabel,
      });
    }
    return items;
  }, [pageHeader?.customerName, pageHeader?.eventTypeLabel, headerVenueLabel]);

  const drawerTitle = (
    <Box sx={{ minWidth: 0, pr: 1 }}>
      <Breadcrumbs
        separator="›"
        aria-label="breadcrumb"
        sx={{
          mb: headerMetaItems.length ? 0.35 : 0,
          '& .MuiBreadcrumbs-separator': { mx: 0.75, color: 'inherit', opacity: 0.7 },
          '& .MuiBreadcrumbs-li': { color: 'inherit' },
        }}>
        <Typography
          component="span"
          sx={{ fontSize: '0.95rem', fontWeight: 500, lineHeight: 1.35, color: 'inherit', opacity: 0.85 }}>
          {strings.new_booking_breadcrumb_booking}
        </Typography>
        <Typography component="span" sx={{ fontSize: '0.95rem', fontWeight: 600, lineHeight: 1.35, color: 'inherit' }}>
          {strings.new_booking_breadcrumb_events_venues}
        </Typography>
      </Breadcrumbs>
      {headerMetaItems.length ? (
        <Stack
          direction="row"
          flexWrap="wrap"
          alignItems="center"
          useFlexGap
          spacing={0.75}
          sx={{ rowGap: 0.25, color: 'inherit', opacity: 0.9 }}>
          {headerMetaItems.map((item, index) => (
            <Stack
              key={`${item.label}-${index}`}
              direction="row"
              alignItems="center"
              spacing={0.5}
              sx={{ minWidth: 0 }}>
              {index > 0 ? (
                <Typography
                  component="span"
                  sx={{ fontSize: '0.8rem', lineHeight: 1, mr: 0.25, opacity: 0.7 }}
                  aria-hidden>
                  ·
                </Typography>
              ) : null}
              <Box sx={{ display: 'inline-flex', flexShrink: 0, lineHeight: 0, opacity: 0.9 }}>{item.icon}</Box>
              <Typography
                noWrap
                sx={{ fontSize: '0.8rem', fontWeight: 500, lineHeight: 1.35, color: 'inherit' }}>
                {item.label}
              </Typography>
            </Stack>
          ))}
        </Stack>
      ) : null}
    </Box>
  );

  const handleLineItemChange = (field: string, value: any, formData: FormDataModel) => {
    if (field === 'startDate') {
      if (dayjs(value).isValid()) {
        lineItemFormRef.current?.handleChange(value, 'endDate', { error: false, message: '' });
        setLineItemsPageResponse((prev: any) => ({
          ...prev,
          startDate: value,
          endDate: value,
        }));
      }
    } else if (field === 'startTime' || field === 'endTime') {
      const nextStart = field === 'startTime' ? value : formData.startTime;
      const nextEnd = field === 'endTime' ? value : formData.endTime;
      const startParsed = parseTimeValue(nextStart);
      const endParsed = parseTimeValue(nextEnd);

      if (hourlyPricing) {
        if (value === '') {
          setLineItemsPageResponse((prev: any) => ({ ...prev, [field]: value }));
        } else {
          const rate = getEffectivePricePerHour(pricing, defaultPricePerHour, lineItemsPageResponse?.pricePerHour);
          if (startParsed && endParsed && rate) {
            const diff = endParsed.diff(startParsed, 'hours', true);
            if (diff <= 0) {
              setMessage({
                display: true,
                severity: 'error',
                message: strings.timePickerValidation,
              });
              setLineItemsPageResponse((prev: any) => ({ ...prev, [field]: value }));
            } else {
              setLineItemsPageResponse((prev: any) => ({ ...prev, [field]: value }));
            }
          } else {
            setLineItemsPageResponse((prev: any) => ({ ...prev, [field]: value }));
          }
        }
      } else {
        setLineItemsPageResponse((prev: any) => ({ ...prev, [field]: value }));
      }

      const startTimeApi = formatTimeForApi(nextStart);
      const endTimeApi = formatTimeForApi(nextEnd);
      if (corporateBooking === 'true' && startTimeApi && endTimeApi) {
        (async () => {
          await getCorporateRates({
            branchId,
            startDate: formData.startDate,
            endDate: formData.endDate,
            startTime: startTimeApi,
            endTime: endTimeApi,
          });
        })();
      }
    } else if (field === 'pricePackage') {
      const defaultPrice = (pricePackageList ?? []).find((p: any) => p.value === value)?.pricePerPerson;
      setDefaultPricePerPerson(defaultPrice);
      setLineItemsPageResponse((prevState: any) => ({
        ...prevState,
        [field]: value,
        customPricePackageName: isCustomizePackage(value, pricePackageList) ? prevState.customPricePackageName : '',
      }));
    } else {
      if (field === 'venue') {
        setLineItemsPageResponse((prev: any) => ({
          ...prev,
          pricePackage: '',
          customPricePackageName: '',
          layoutObj: '',
          serviceChargePercentage: '',
          serviceCharge: null,
          taxGroupOnGuestCharge: '',
          taxGroupOnHallRental: '',
          layout: '',
          venue: value,
        }));
        setTaxGroupList([]);
        setServiceChargeList([]);
        setBookedAddOns([]);
        setLayoutList([]);
        setPricePackageList([]);
        setAddonsList([]);
        setSelectedVenue(value as string);
      }
      setLineItemsPageResponse((prev: any) => ({ ...prev, [field]: value }));
    }
  };

  const isTimeMissing = (time: unknown) => parseTimeValue(time as string) == null;

  const handleSave = async (e: any) => {
    e.preventDefault();
    if (savingRef.current || isSavingLineItem) return;
    savingRef.current = true;
    setIsSavingLineItem(true);
    setMessage({ display: false, severity: null, message: '' });
    let saved = false;
    try {
    const part1 = lineItemFormRef.current as
      | {
          getFormData: () => { formData: FormDataModel; isFormValid: boolean };
          handleChange: (value: string | boolean, field: string, error?: { error: boolean; message: string }) => void;
        }
      | null
      | undefined;
    const part2 = lineItemTaxFormRef.current as
      | { getFormData: () => { formData: FormDataModel; isFormValid: boolean } }
      | null
      | undefined;
    if (!part1) return;
    const { formData: data1, isFormValid: valid1 } = part1.getFormData();
    const { formData: data2, isFormValid: valid2 } = part2
      ? part2.getFormData()
      : { formData: {} as FormDataModel, isFormValid: true };
    let formData: FormDataModel = { ...data1, ...data2 };
    let isFormValid = valid1 && valid2;
    let flag = 0;
    if (!hourlyPricing && formData.pricePackage === '') {
      formData.pricePackageError = true;
      flag = 1;
    }
    if (isTimeMissing(data1.startTime)) {
      part1.handleChange('', 'startTime', {
        error: true,
        message: `${strings.requiredText}: ${strings.timeSlot_column_startTime}`,
      });
      flag = 1;
    }
    if (isTimeMissing(data1.endTime)) {
      part1.handleChange('', 'endTime', {
        error: true,
        message: `${strings.requiredText}: ${strings.timeSlot_text_endTime}`,
      });
      flag = 1;
    }
    if (flag) isFormValid = false;
    const data = removeErrorFieldsFromValues(formData);
    setHasError(false);
    if (isFormValid) {
      if (data.startDate === 'Invalid Date' || data.endDate === 'Invalid Date') {
        return;
      }
      const newStartDate = dayjs(data.startDate as string);
      const newEndDate = dayjs(data.endDate as string);
      // let bookedAddOnsMaxCountError = [] as any;
      let bookedAddOnsNumberError = [] as any;
      let bookedAddOnsPriceError = [] as any;
      let bookedAddOnsQuantityLimitError = [] as any;
      bookedAddOns.forEach((item: any) => {
        if (item.type === 'PER_QUANTITY' && !item.quantity) item = { ...item, quantity: 0 };

        if (item.type === 'PER_QUANTITY' && item.quantity < 1) {
          bookedAddOnsNumberError.push(item.name);
        }
        // else if (item.type === 'PER_QUANTITY' && item.quantity > item.maxCount) {
        //   bookedAddOnsMaxCountError.push(item.name);
        // }
        else if (item.type === 'PER_QUANTITY' && item.quantity > 100000) {
          bookedAddOnsQuantityLimitError.push(item.name);
        } else if (item.price < 0) {
          bookedAddOnsPriceError.push(item.name);
        }
      });
      if (newEndDate.isBefore(newStartDate)) {
        setMessage({
          display: true,
          severity: 'error',
          message: strings.datePickerValidation,
        });
      } else {
        const startParsed = parseTimeValue(data.startTime as string);
        const endParsed = parseTimeValue(data.endTime as string);
        if (startParsed && endParsed && !startParsed.isBefore(endParsed)) {
          setMessage({
            display: true,
            severity: 'error',
            message: strings.timePickerValidation,
          });
        } else if (bookedAddOnsNumberError.length > 0) {
          setMessage({
            display: true,
            severity: 'error',
            message: `${bookedAddOnsNumberError.join(', ')} ${strings.addOn_Error}`,
          });
        } else if (bookedAddOnsQuantityLimitError.length > 0) {
          setMessage({
            display: true,
            severity: 'error',
            message: `${bookedAddOnsQuantityLimitError.join(', ')} ${strings.addOn_QuantityLimitError}`,
          });
        } else if (bookedAddOnsPriceError.length > 0) {
          setMessage({
            display: true,
            severity: 'error',
            message: `${bookedAddOnsPriceError.join(', ')} ${strings.addOnPriceError}`,
          });
        } else {
        const array = [...lineItems];
        // Mix the form data with the pricing override panel's state so the
        // backend payload carries both the schedule/venue choices and the
        // strategy + override flags + values.
        const newData = {
          ...data,
          eventSubtype:
            typeof data.eventSubtype === 'object'
              ? (data.eventSubtype as any)?.label || (data.eventSubtype as any)?.value || ''
              : (data.eventSubtype as any),
          startTime: formatTimeForApi(data.startTime as string),
          endTime: formatTimeForApi(data.endTime as string),
          pricePackage: hourlyPricing ? null : data.pricePackage,
          pricePackageId: hourlyPricing ? null : data.pricePackageId,
          customPricePackageName:
            !hourlyPricing && isCustomizePackage(data.pricePackage, pricePackageList)
              ? normalizeCustomPricePackageName(data.customPricePackageName)
              : null,
          pricePackageObj: hourlyPricing
            ? null
            : {
                value: data?.pricePackage,
                label: (pricePackageList ?? []).find((i: any) => i.value === data?.pricePackage)?.label,
              },
          layoutObj: {
            value: data.layout,
            label: (layoutList ?? []).find((i: any) => i.value === data.layout)?.label,
          },
          addOns: bookedAddOns,
          serviceCharge: data.serviceCharge?.hasOwnProperty?.value === null ? null : data.serviceCharge,
          // Send overridden numbers only; null tells the backend to use venue defaults.
          pricePerPerson: pricing.overridePricePerPerson ? pricing.pricePerPerson : null,
          minimumRevenue: pricing.overrideMinimumRevenue ? pricing.minimumRevenue : null,
          hallRental: pricing.overrideHallRental ? pricing.hallRental : null,
          pricePerHour: hourlyPricing && pricing.overridePricePerHour ? pricing.pricePerHour : null,
          // Persist override flags so re-opening the drawer pre-fills the panel correctly.
          overridePricePerPerson: pricing.overridePricePerPerson,
          overrideMinimumRevenue: pricing.overrideMinimumRevenue,
          overrideHallRental: pricing.overrideHallRental,
          overridePricePerHour: pricing.overridePricePerHour,
        };

        let lineItemTotal: string | null = costingPageResponse?.total ?? null;
        let lineItemTotalUnformatted: number | null = costingPageResponse?.totalUnformatted ?? null;
        if (resolveLineItemTotalOnSave) {
          const totals = await resolveLineItemTotalOnSave(newData, selectedRowId);
          if (totals && !totals.ok) {
            setMessage({
              display: true,
              severity: 'error',
              message: totals.message,
            });
            return;
          }
          if (totals?.ok) {
            lineItemTotal = totals.total;
            lineItemTotalUnformatted = totals.totalUnformatted;
          }
        }

        const newDataWithTotal = {
          ...newData,
          lineItemTotal,
          lineItemTotalUnformatted,
        };
        if (selectedRowId !== null) {
          array[selectedRowId] = { ...array[selectedRowId], ...newDataWithTotal };
          setSelectedRowId(null);
        } else {
          array.push({ ...newDataWithTotal, id: maxCount + 1 });
        }
        setMaxCount(maxCount + 1);
        setLineItems(array);
        resetLineItemFormState();
        saved = true;
        setOpenDrawer(false);
        }
      }
    } else {
      const missing = missingLineItemCostingLabels(data1, hourlyPricing, {
        venue: strings.new_booking_venue,
        startDate: strings.new_booking_start_date,
        endDate: strings.new_booking_end_date,
        guests: strings.new_booking_number_of_guests,
        partOfDay: strings.timeSlot_column_partOfDay,
        status: strings.new_booking_status,
        startTime: strings.timeSlot_column_startTime,
        endTime: strings.timeSlot_text_endTime,
        pricePackage: strings.new_booking_package,
      });
      flushSync(() => setHasError(true));
      if (missing.length > 0) {
        setMessage({
          display: true,
          severity: 'error',
          message: `${strings.requiredText}: ${missing.join(', ')}`,
        });
      }
      setTimeout(() => {
        const element = document.querySelector('.Mui-error');
        element?.scrollIntoView({ behavior: 'smooth' });
      }, 200);
    }
    } finally {
      if (!saved) {
        savingRef.current = false;
        setIsSavingLineItem(false);
      }
    }
  };

  const lineItemsFormComponent = () => {
    const selectedPackageLabel =
      (pricePackageList ?? []).find((p: any) => String(p.value) === String(lineItemsPageResponse?.pricePackage))?.label ??
      null;
    const selectedVenueLabel =
      (venueList ?? []).find((v: any) => String(v.value) === String(lineItemsPageResponse?.venue))?.label ?? null;
    const selectedVenueMaxCapacity =
      venueMaxCapacity ??
      (venueList ?? []).find((v: any) => String(v.value) === String(lineItemsPageResponse?.venue))?.maxCapacity ??
      null;
    const selectedLayoutMaxCapacity =
      (layoutList ?? []).find((l: any) => String(l.value) === String(lineItemsPageResponse?.layout))?.maxCapacity ??
      null;
    const selectedLayoutLabel =
      (layoutList ?? []).find((l: any) => String(l.value) === String(lineItemsPageResponse?.layout))?.label ?? null;

    return (
      <>
        <Grid container spacing={1.25} sx={{ mt: 0, mb: 0 }} alignItems="flex-start">
          <Form
            dropdownData={{ layout: layoutList ?? [] }}
            hasError={hasError}
            describeRequiredError
            ref={lineItemFormRef as RefObject<Form>}
            model={lineItemsForm({
              strings,
              venues: venueList ?? [],
              statuses: statusList ?? [],
              layouts: layoutList ?? [],
              pricepackages: pricePackageList ?? [],
              isDisabled: false,
              serviceChargeDropdown,
              serviceChargeList,
              selectedStartDate,
              selectedEndDate,
              hourlyPricing,
              isOdc,
              partOfDays,
              eventSubtypeOptions,
              excessGuestsPercentage: lineItemsPageResponse?.excessGuestsPercentage,
              selectedPricePackage: lineItemsPageResponse?.pricePackage,
              pastBookingWindowDays,
              layoutMaxCapacity: selectedLayoutMaxCapacity,
              layoutName: selectedLayoutLabel,
              layoutSelected: Boolean(lineItemsPageResponse?.layout),
              venueMaxCapacity: selectedVenueMaxCapacity,
              venueName: selectedVenueLabel,
              venueSelected: Boolean(lineItemsPageResponse?.venue),
            })}
            values={lineItemsPageResponse}
            onFocus={handleLineItemFormChange}
            onChange={handleLineItemChange}
          />
        </Grid>
        {/* PricingStrategySection sits directly under the "Pricing Configuration"
            section header rendered as the last item of Part 1 above. */}
        <Grid container spacing={1.25} sx={{ mt: 0, mb: 0 }}>
          <PricingStrategySection
            values={pricing}
            defaults={{
              pricePerPerson: defaultPricePerPerson,
              minimumRevenue: defaultMinimumRevenue,
              pricePerHour: defaultPricePerHour ?? lineItemsPageResponse?.pricePerHour,
              hallRental: hourlyPricing ? hourlyTotalRental : fixedHallRental,
              hourlyRentalBreakdown: hourlyPricingBreakdown,
              packageName: selectedPackageLabel,
              venueName: selectedVenueLabel,
            }}
            onChange={handlePricingChange}
            restrictPriceOverride={restrictPriceOverride}
            hourlyPricing={hourlyPricing}
          />
        </Grid>
        <Grid container spacing={1.25} sx={{ mt: 0, mb: 0 }} alignItems="flex-start">
          <Form
            dropdownData={{
              taxGroupOnGuestCharge: taxGroupList,
              taxGroupOnHallRental: taxGroupList,
            }}
            hasError={hasError}
            describeRequiredError
            ref={lineItemTaxFormRef as RefObject<Form>}
            model={lineItemsTaxForm({
              strings,
              taxGroups: taxGroupList ?? [],
              venueSelected: Boolean(lineItemsPageResponse?.venue),
            })}
            values={lineItemsPageResponse}
            onChange={handleLineItemChange}
          />
        </Grid>
        {addonsList && Object.keys(addonsList).length > 0 && (
          <NewBookingAddon
            addonsDropdown={[
              ...Object.keys(addonsList)
                .map(key => addonsList[key])
                .flat(),
            ]}
            bookedAddOns={bookedAddOns}
            setBookedAddOns={setBookedAddOns}
          />
        )}
      </>
    );
  };

  return (
    <DrawerMenu
      width={getLineItemDrawerWidth(screenSizeDownSm, screenSizeDownMd)}
      open={openDrawer}
      handleClose={handleClose}
      closeButton
      dropBack={false}
      overlapHeader
      flexColumn
      noPadding
      title={drawerTitle}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          minHeight: 0,
          px: { xs: 1.25, md: 1.75 },
          pt: 1,
          pb: 0,
        }}>
        <Box
          sx={{
            display: 'flex',
            flex: 1,
            flexDirection: { xs: 'column', md: 'row' },
            gap: { xs: 1, md: 1.25 },
            minHeight: 0,
            overflow: { xs: 'auto', md: 'hidden' },
          }}>
          <Box
            sx={{
              width: { xs: '100%', md: '74%' },
              flex: 1,
              minWidth: 0,
              minHeight: 0,
              overflowY: 'auto',
              pb: 1,
            }}>
            <Paper
              elevation={0}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1.5,
                pt: { xs: 1, md: 1.15 },
                px: { xs: 1.25, md: 1.75 },
                pb: { xs: 1.25, md: 1.75 },
              }}>
              {lineItemsFormComponent()}
            </Paper>
          </Box>
          <Box
            sx={{
              width: { xs: '100%', md: '26%' },
              flexShrink: 0,
              position: { md: 'sticky' },
              top: { md: 0 },
              alignSelf: { md: 'flex-start' },
              maxHeight: { md: '100%' },
              overflowY: { md: 'auto' },
            }}>
            <VenueDetailsPageSummary
              pageResponse={costingPageResponse ?? {}}
              loading={costingLoading}
              error={costingError}
            />
          </Box>
        </Box>

        <Paper
          elevation={0}
          sx={{
            flexShrink: 0,
            position: 'sticky',
            bottom: 0,
            zIndex: 1,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1.5,
            px: { xs: 1, md: 1.25 },
            py: 0.75,
            my: 1,
            bgcolor: 'background.paper',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 0.75,
          }}>
          <Button variant="outlined" size="small" onClick={handleClose}>
            {strings.receivable_button_cancel}
          </Button>
          <Button
            variant="contained"
            size="small"
            disabled={isSavingLineItem}
            sx={{ color: '#ffffff' }}
            onClick={e => {
              handleSave(e);
            }}>
            {isSavingLineItem ? strings.new_booking_venue_details_saving : strings.common_button_save}
          </Button>
        </Paper>
      </Box>
    </DrawerMenu>
  );
};

export default LineItemDrawerComponent;
